import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BaseApiService } from 'projects/core/src/lib/services/base-api.service';
import { MatchingPayload } from '../orders/pages/orderform/partials/orderform-date/orderform-date.component';
import { FileSettings } from 'projects/core/src/lib/types/filesettings.type';
import { HelperService } from './helper.service';
import { MoveOrderPayload } from '../pages/admin/planning/orders/move-order/move-order.component';

@Injectable({
  providedIn: 'root'
})
export class ApiService extends BaseApiService {

  constructor(
    protected http: HttpClient,
    private helper: HelperService
  ) {
    super(http);
    this.mode = 'admin';
  }

  // BASE

  init(): Observable<any> {
    return this.http.get<any>(this.getUrl('init'), this.options).pipe(
      catchError(error => {
        return throwError(() => error.error);
      })
    );
  }

  // AUTH

  login(username: string, password: string): Observable<any> {
    return this.http.post<any>(this.getAuthUrl('token'), {
      grant_type: 'password',
      client_id: '2',
      client_secret: this.oauthClientSecret,
      username,
      password,
      scope: ''
    }, this.options).pipe(
      catchError((error) => this.handleLoginError(error))
    );
  }

  refreshToken(token: string): Observable<any> {
    return this.http.post<any>(this.getAuthUrl('token'), {
      grant_type: 'refresh_token',
      client_id: '2',
      client_secret: this.oauthClientSecret,
      refresh_token: token,
      scope: ''
    }, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getUserProfile(): Observable<any> {
    return this.http.get<any>(this.getUrl('user-profile'), this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  sendOnetimePassword(email: string): Observable<any> {
    return this.http.post<any>(this.getUrl('onetime-password'), { email }, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  renewPassword(payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('renew-password'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  loadPrivacy(): Observable<any> {
    return this.http.get<any>(this.getUrl('data/privacy'), this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  savePrivacy(): Observable<any> {
    return this.http.post<any>(this.getUrl('data/privacy'), {}, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  // CRUD operations

  getModels(type: string, params: any = null): Observable<any> {
    const options = {...this.options};
    if (params !== null) {
      options.params = params;
    }
    return this.http.get<any>(this.getUrl('data/' + type), options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getModel(type: string, id: number, params: any = null): Observable<any> {
    const options = {...this.options};
    if (params !== null) {
      options.params = params;
    }
    return this.http.get<any>(this.getUrl('data/' + type + '/' + id), options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveModel(type: string, id: number, payload: any): Observable<any> {
    if (id !== null) {
      return this.http.patch<any>(this.getUrl('data/' + type + '/' + id), payload, this.options).pipe(
        catchError((error) => this.handleError(error))
      );
    } else {
      return this.http.post<any>(this.getUrl('data/' + type), payload, this.options).pipe(
        catchError((error) => this.handleError(error))
      );
    }
  }

  deleteModel(type: string, id: number, params: {[key: string]: any} = null): Observable<any> {
    const options = {...this.options};
    if (params !== null) {
      options.params = {...options.params, ...params};
    }
    return this.http.delete<any>(this.getUrl('data/' + type + '/' + id), options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  // SPECIAL operations

  getTemplateSettings(mode: string): Observable<any> {
    return this.http.get<any>(this.getUrl('data/template-settings/' + mode), this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveTemplateSettings(mode: string, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/template-settings/' + mode), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveAcPersons(id: number, payload: any): Observable<any> {
    return this.http.patch<any>(this.getUrl('data/acs/' + id + '/persons'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  sendAcInvitations(id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/acs/' + id + '/invitations'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  sendOnlinetestReminder(id: number, candidateAcOnlinetestId: number): Observable<any> {
    return this.http.post<any>(this.getUrl('data/acs/' + id + '/onlinetest-reminder/' + candidateAcOnlinetestId), {}, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  createAcFromTemplate(id: number): Observable<any> {
    return this.http.post<any>(this.getUrl('data/templates/' + id + '/makeac'), {}, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveExercisepartsOrder(id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/exercises/' + id + '/exercisepartorder'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveExerciseCompetences(id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/exercises/' + id + '/competences'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  deleteExercisepartCompetence(exercisepartId: number, competenceId: number, inherit: boolean = false): Observable<any> {
    const options = {...this.options};
    if (inherit === true) {
      options.params = { inherit };
    }
    return this.http.delete<any>(this.getUrl('data/exerciseparts/' + exercisepartId + '/competences/' + competenceId), options).pipe(
      catchError((error) => this.handleError(error))
      );
  }

  saveElementsOrder(id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/exercises/' + id + '/elementorder'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveElementContent(id: number, content: any, autoplay: number = null, inherit: boolean = false): Observable<any> {
    return this.http.post<any>(this.getUrl('data/elements/' + id + '/content'), { content, autoplay, inherit }, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveElementPermissions(id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/elements/' + id + '/permissions'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  duplicateElement(id: number, inherit: boolean = false): Observable<any> {
    return this.http.post<any>(this.getUrl('data/elements/' + id + '/duplicate'), { inherit }, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveAcExercise(mode: string, id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/' + mode + '/' + id + '/exercises'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  deleteAcExercise(mode: string, acId: number, exerciseId: number, inherit: boolean = false): Observable<any> {
    const options = {...this.options};
    if (inherit === true) {
      options.params = { inherit };
    }
    return this.http.delete<any>(this.getUrl('data/' + mode + '/' + acId + '/exercises/' + exerciseId), options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveAcOnlinetest(mode: string, id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/' + mode + '/' + id + '/onlinetests'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  deleteAcOnlinetest(mode: string, acId: number, onlinetestId: number, inherit: boolean = false): Observable<any> {
    const options = {...this.options};
    if (inherit === true) {
      options.params = { inherit };
    }
    return this.http.delete<any>(this.getUrl('data/' + mode + '/' + acId + '/onlinetests/' + onlinetestId), options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getTemplateCategories(): Observable<any> {
    return this.http.get<any>(this.getUrl('data/template_categories'), this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  copyTemplate(id: number): Observable<any> {
    return this.http.post<any>(this.getUrl('data/templates/' + id + '/copy'), {}, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  copyAc(id: number): Observable<any> {
    return this.http.post<any>(this.getUrl('data/acs/' + id + '/copy'), {}, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  cancelAc(id: number, reason: string = null): Observable<any> {
    return this.http.post<any>(this.getUrl('data/acs/' + id + '/cancel'), { reason }, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  orderAc(id: number, force: boolean = false): Observable<any> {
    return this.http.post<any>(this.getUrl('data/acs/' + id + '/order'), { force }, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  deactivateTemplate(id: number): Observable<any> {
    return this.http.post<any>(this.getUrl('data/templates/' + id + '/deactivate'), {}, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  activateTemplate(id: number): Observable<any> {
    return this.http.post<any>(this.getUrl('data/templates/' + id + '/activate'), {}, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  deactivateExercise(id: number): Observable<any> {
    return this.http.post<any>(this.getUrl('data/exercises/' + id + '/deactivate'), {}, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  activateExercise(id: number): Observable<any> {
    return this.http.post<any>(this.getUrl('data/exercises/' + id + '/activate'), {}, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveSlots(mode: string, id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/' + mode + '/' + id + '/slots'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getRecalculatedTiming(id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/templates/' + id + '/timing'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  copyExercise(id: number): Observable<any> {
    return this.http.post<any>(this.getUrl('data/exercises/' + id + '/copy'), {}, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveCompetencesOrder(payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/competences/order'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveOnlinetestCompetence(id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/onlinetests/' + id + '/competence'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  deleteOnlinetestCompetence(id: number, competence: number): Observable<any> {
    return this.http.delete<any>(this.getUrl('data/onlinetests/' + id + '/competence/' + competence), this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveTemplateTodos(id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/templates/' + id + '/todos'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveTemplateTodosOrder(id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/templates/' + id + '/todos/order'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveOrderTodos(id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/orders/todos/' + id), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  // toolbox

  getToolboxFormData(): Observable<any> {
    return this.http.get<any>(this.getUrl('data/toolbox/formdata'), this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveToolboxCredentials(id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/toolbox/' + id + '/credentials'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  resendToolboxReport(id: number): Observable<any> {
    return this.http.post<any>(this.getUrl('data/toolbox/' + id + '/resendreport'), {}, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  // employees

  searchEmployees(payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/employees'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  // contents

  duplicateTextblock(id: string | number): Observable<any> {
    return this.http.post<any>(this.getUrl('data/textblocks/' + id + '/duplicate'), {}, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveTextContents(type: string, id: string | number, contents: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/' + type + '/' + id + '/contents'), contents, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  showTexttemplatePreview(type: string, id: string | number, language: string): Observable<any> {
    return this.http.post<any>(this.getUrl('data/' + type + '/' + id + '/preview/show'), { language }, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  sendTexttemplatePreview(type: string, id: string | number, language: string, recipient: string): Observable<any> {
    return this.http.post<any>(this.getUrl('data/' + type + '/' + id + '/preview/send'), { language, recipient }, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getTexttemplateFiles(id: string | number): Observable<any> {
    return this.http.get<any>(this.getUrl('data/texttemplates/' + id + '/files'), this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveTexttemplateFile(id: string | number, language_id: number, file_ids: Array<number>): Observable<any> {
    return this.http.post<any>(this.getUrl('data/texttemplates/' + id + '/files'), { file_ids, language_id }, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  deleteTexttemplateFile(id: string | number, language_id: number, file_id: string | number): Observable<any> {
    return this.http.delete<any>(this.getUrl('data/texttemplates/' + id + '/files/' + language_id + '/' + file_id), this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  // appointments

  getAvailabilities(): Observable<any> {
    return this.http.get<any>(this.getUrl('data/appointments/availabilities'), this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveAppointments(mode: string, appointments: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/appointments/' + mode), { appointments }, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getAppointmentSetup(mode: string = null): Observable<any> {
    const options = {...this.options};
    if (mode !== null) {
      options.params = { mode };
    }
    return this.http.get<any>(this.getUrl('data/appointments/setup'), options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getAppointmentObservers(): Observable<any> {
    return this.http.get<any>(this.getUrl('data/appointments/observers'), this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  // orders

  createNewOrder(payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/orders/new'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getOrderformSetupData(inactive = false): Observable<any> {
    const options = {...this.options};
    if (inactive === true) {
      options.params = { inactive };
    }
    return this.http.get<any>(this.getUrl('data/ordersdata/setup'), options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getOrderMatchings(payload: MatchingPayload): Observable<any> {
    return this.http.post<any>(this.getUrl('data/ordersdata/matchings'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getOrderMovingData(id: number): Observable<any> {
    return this.http.get<any>(this.getUrl('data/ordersdata/moving/' + id), this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getOrderMovingMatchings(id: number): Observable<any> {
    return this.http.post<any>(this.getUrl('data/ordersdata/moving/matchings'), { ac: id }, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  moveOrder(id: number, payload: MoveOrderPayload): Observable<any> {
    return this.http.post<any>(this.getUrl('data/ordersdata/moving/' + id + '/move'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  finishOrder(id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/orders/finish/' + id), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  finishResultreport(id: number, report: number): Observable<any> {
    return this.http.post<any>(this.getUrl('data/orders/finish-resultreport/' + id + '/' + report), {}, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getAITemplateTestValue(): Observable<any> {
    return this.http.get<any>(this.getUrl('data/aitemplates/testvalue'), this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  runAITemplateTest(id: number, json: string): Observable<any> {
    const options = {...this.options};
    options.responseType = 'arraybuffer';
    return this.http.post<any>(this.getUrl('data/aitemplates/test/' + id), { json }, options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  // files

  uploadFiles(files: Array<File>, type: string = null, id: number = null, settings: FileSettings = null, multi: boolean = false, inherit: boolean = false): Observable<any> {
    const headers = new HttpHeaders();
    headers.append('Accept', 'application/json');
    headers.append('Content-Type', 'multipart/form-data');
    const payload: FormData = new FormData();
    if (type !== null) {
      payload.append('type', type);
    }
    if (id !== null) {
      payload.append('id', '' + id);
    }
    if (settings !== null) {
      payload.append('settings', JSON.stringify(settings));
    }
    if (multi !== false) {
      payload.append('multi', '1');
    }
    if (inherit !== false) {
      payload.append('inherit', '1');
    }
    files.forEach((file: File, index: number) => {
      payload.append('files_' + index, file);
    });
    return this.http.post<any>(this.getUrl('data/files/upload'), payload, {
      headers
    }).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  downloadFile(fileId: number, type: string = null, ownerId: number = null): Observable<any> {
    const options = {...this.options};
    options.responseType = 'arraybuffer';
    return this.http.get<any>(this.getUrl(this.getFileUrl(fileId, type, ownerId)), options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  deleteFile(fileId: number, type: string = null, ownerId: number = null, inherit: boolean = false): Observable<any> {
    const options = {...this.options};
    if (inherit === true) {
      options.params = { inherit };
    }
    return this.http.delete<any>(this.getUrl(this.getFileUrl(fileId, type, ownerId)), options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  private getFileUrl(fileId: number, type: string = null, ownerId: number = null): string {
    let url = 'data/files/' + fileId;
    if (type !== null) {
      url += '/' + type;
    }
    if (ownerId !== null) {
      url += '/' + ownerId;
    }
    return url;
  }

  uploadEditorImage(id: number, file: any, filename: string): Observable<any> {
    const headers = new HttpHeaders();
    headers.append('Accept', 'application/json');
    headers.append('Content-Type', 'multipart/form-data');
    const payload: FormData = new FormData();
    payload.append('image', file);
    payload.append('filename', filename);
    return this.http.post<any>(this.getUrl('data/elements/' + id + '/images'), payload, {
      headers
    }).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getVideos(): Observable<any> {
    return this.http.get<any>(this.getUrl('data/videos')).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  // statistics

  getStatisticsFilters(section: string): Observable<any> {
    return this.http.get<any>(this.getUrl('data/statistics/' + section + '/filters')).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getStatisticsData(section: string, type: string, filters: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/statistics/' + section + '/data/' + type), filters).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getResourcesFilters(): Observable<any> {
    return this.http.get<any>(this.getUrl('data/statistics/resources/filters')).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  getResourcesData(filters: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/statistics/resources/data'), filters).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  // user


  saveUserLocations(id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/users/' + id + '/locations'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }


  saveUserTemplates(id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/users/' + id + '/templates'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }


  // reportings

  getReporting(type: string): Observable<any> {
    return this.http.get<any>(this.getUrl('data/reportings/' + type), this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }


  // profile

  getMyProfile(): Observable<any> {
    return this.http.get<any>(this.getUrl('data/my-profile'), this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveMyProfile(payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/my-profile/basedata'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveMyLocations(payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/my-profile/locations'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveMyTemplates(payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/my-profile/templates'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }


  // role/rights

  getRights(): Observable<any> {
    return this.http.get<any>(this.getUrl('data/rights'), this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveRights(payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/rights'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveRoleDataScopes(id: number, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/roles/' + id + '/data-scopes'), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  saveDataScopeEntries(id: number, dimension: string, payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('data/data-scopes/' + id + '/entries/' + dimension), payload, this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  // message orders

  getMessageorderOptions(): Observable<any> {
    return this.http.get<any>(this.getUrl('data/messageorder-options'), this.options).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  // candidate moving

  public getMovingAcs(acId: number): Observable<any> {
    return this.http.get<any>(this.getUrl('data/orders/moving/acs/' + acId))
    .pipe(
      catchError((error) => this.handleError(error))
    );
  }

  public moveCandidateToOtherAc(source: number, target: number, candidate: string): Observable<any> {
    return this.http.post<any>(this.getUrl('data/orders/moving/candidate'), {
      source,
      target,
      candidate
    })
    .pipe(
      catchError((error) => this.handleError(error))
    );
  }

  // billing

  public getBillingStatus(): Observable<any> {
    return this.http.get<any>(this.getUrl('data/billing-status'))
    .pipe(
      catchError((error) => this.handleError(error))
    );
  }

  // errors

  protected handleError(error: HttpErrorResponse): any {
    return throwError(() => {
      return this.helper.throwApiError(error);
    });
  }

  protected handleLoginError(error: HttpErrorResponse): any {
    return throwError(() => {
      return {
        status: error.status,
        message: error.error?.message || null
      };
    });
  }

}
